import React from 'react';
import { resolveCanvasInlineEditState } from './inline-edit-state.js';

const firstTruthy = (...readers) => readers.reduce((result, read) => result || read(), undefined);
const everyTruthy = (...readers) => readers.reduce((result, read) => result && read(), true);

function resetInlineEditState({
  lastInlineTabKeyRef,
  lastSeedRef,
  setCanvasDraftEditing,
  showCanvasPanel,
}) {
  if (!showCanvasPanel || !lastInlineTabKeyRef.current) {
    return;
  }
  lastInlineTabKeyRef.current = '';
  lastSeedRef.current = { content: '', tabKey: '' };
  setCanvasDraftEditing?.(false);
}

export function useCanvasInlineEditEffect(options) {
  const { canvasActiveTabKey, canvasDraftEditing, canvasDraftEditorValue, canvasManualControlsEnabled, canvasTabs, currentContent, setCanvasDraftEditing, setCanvasDraftEditorValue, showCanvasPanel } = { canvasActiveTabKey: '', canvasDraftEditing: false, canvasDraftEditorValue: '', canvasManualControlsEnabled: false, canvasTabs: [], currentContent: '', showCanvasPanel: false, ...Object.fromEntries(Object.entries(Object(options)).filter(([, value]) => value !== undefined)) };
  const inlineEditState = React.useMemo(() => resolveCanvasInlineEditState({
    activeTabKey: canvasActiveTabKey,
    canvasManualControlsEnabled: canvasManualControlsEnabled && showCanvasPanel,
    canvasTabs,
  }), [
    canvasActiveTabKey,
    canvasManualControlsEnabled,
    canvasTabs,
    showCanvasPanel,
  ]);
  const lastInlineTabKeyRef = React.useRef('');
  const lastSeedRef = React.useRef({ content: '', tabKey: '' });

  React.useEffect(() => {
    if (!inlineEditState.enabled) {
      resetInlineEditState({ lastInlineTabKeyRef, lastSeedRef, setCanvasDraftEditing, setCanvasDraftEditorValue, showCanvasPanel });
      return;
    }
    const tabKey = firstTruthy(() => inlineEditState.activeTabKey, () => 'requirement');
    const content = String(firstTruthy(() => currentContent, () => '')).trim();
    const editorValue = String(firstTruthy(() => canvasDraftEditorValue, () => '')).trim();
    const shouldSeedLateContent = (
      everyTruthy(() => lastSeedRef.current.tabKey === tabKey, () => !lastSeedRef.current.content, () => Boolean(content), () => !editorValue)
    );
    if ((!canvasDraftEditing && !editorValue) || shouldSeedLateContent) {
      setCanvasDraftEditorValue?.(content);
      lastSeedRef.current = { content, tabKey };
    }
    lastInlineTabKeyRef.current = tabKey;
    if (!canvasDraftEditing) {
      setCanvasDraftEditing?.(true);
    }
  }, [
    canvasDraftEditing,
    canvasDraftEditorValue,
    currentContent,
    inlineEditState.activeTabKey,
    inlineEditState.enabled,
    setCanvasDraftEditing,
    setCanvasDraftEditorValue,
    showCanvasPanel,
  ]);

  return inlineEditState;
}

export default useCanvasInlineEditEffect;
